import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock, ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import { setToken, isAuthed } from "../lib/auth";
import { API } from "../lib/api";

export default function AdminLoginPage() {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();
  const loc = useLocation();

  // Already authed? Send them straight to admin home (runs once on mount).
  useEffect(() => {
    if (isAuthed()) {
      const from = loc.state?.from || "/";
      nav(from, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setError("");
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!r.ok) {
        setError(r.status === 401 ? "Wrong password" : "Login failed");
        setSubmitting(false);
        return;
      }
      const data = await r.json();
      setToken(data.token, data.expires_at);
      const from = loc.state?.from || "/";
      nav(from, { replace: true });
    } catch (err) {
      setError("Network error");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-[#D4FF00] flex items-center justify-center">
            <ShieldCheck size={22} weight="bold" color="#0A0A0A" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4FF00]" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
              PROTECTED AREA
            </div>
            <div className="text-2xl tracking-tight" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
              helios<span className="text-[#0047FF]">/</span>leads
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white text-[#0A0A0A] border-2 border-[#D4FF00] shadow-[6px_6px_0px_#0047FF] p-6" data-testid="admin-login-form">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#525252] mb-2" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
            ADMIN PASSWORD
          </div>
          <div className="relative">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              disabled={submitting}
              data-testid="admin-password-input"
              className="w-full border-2 border-[#0A0A0A] px-3 py-3 text-lg outline-none focus:border-[#0047FF] disabled:bg-[#F4F4F2] font-mono"
              placeholder="••••••••••"
              autoComplete="current-password"
              spellCheck="false"
            />
            <Lock size={16} weight="bold" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#525252]" />
          </div>
          {error && (
            <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 px-2 py-1" data-testid="admin-login-error" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting || !password}
            data-testid="admin-login-submit"
            className="mt-4 w-full bg-[#0A0A0A] text-[#D4FF00] px-4 py-3 text-xs tracking-[0.25em] uppercase shadow-[2px_2px_0px_#0047FF] hover:opacity-90 active:translate-y-[2px] active:translate-x-[2px] active:shadow-none disabled:bg-[#525252] disabled:shadow-none inline-flex items-center justify-center gap-2 transition-all duration-150"
            style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
          >
            {submitting ? "AUTHENTICATING…" : (<>UNLOCK <ArrowRight size={12} weight="bold" /></>)}
          </button>
          <div className="mt-6 text-[10px] tracking-[0.2em] uppercase text-[#525252] flex items-center gap-1.5" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
            <Lock size={9} weight="bold" /> Session valid for 30 days on this device
          </div>
        </form>

        <div className="mt-4 text-center">
          <a href="/products" className="text-xs text-[#525252] hover:text-white tracking-[0.2em] uppercase" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
            ← Public storefront
          </a>
        </div>
      </div>
    </div>
  );
}
